import React, { useState } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Typography,
    Avatar,
    Box,
    TextField,
    List,
    ListItem,
    ListItemAvatar,
    ListItemText,
    Divider,
    Badge,
    Stack
} from '@mui/material';
import { Group as GroupIcon } from '@mui/icons-material';
import { Group } from '../../../Models/Group';
import { User } from '../../../Models/User';

interface GroupDetailsModalProps {
    open: boolean;
    group: Group | null;
    members: User[];
    onClose: () => void;
    onEdit: (group: Group) => void;
    onDelete: (uuid: string) => void;
    onClearMembers?: (uuid: string) => void;
}

const GroupDetailsModal: React.FC<GroupDetailsModalProps> = ({ open, group, members, onClose, onEdit, onDelete, onClearMembers }) => {
    const [search, setSearch] = useState('');

    if (!group) return null;

    const filteredMembers = members.filter(
        m => m.firstname.toLowerCase().includes(search.toLowerCase()) ||
            m.lastname.toLowerCase().includes(search.toLowerCase()) ||
            m.email.toLowerCase().includes(search.toLowerCase())
    );

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="md" PaperProps={{
            sx: {
                borderRadius: '20px',
                overflow: 'hidden'
            }
        }}>
            <DialogTitle sx={{
                background: 'linear-gradient(135deg, rgba(102, 126, 234, 0.05) 0%, rgba(118, 75, 162, 0.05) 100%)',
                borderBottom: '1px solid rgba(102, 126, 234, 0.1)',
                display: 'flex',
                alignItems: 'center',
                gap: 1.5
            }}>
                <Box sx={{
                    width: 40,
                    height: 40,
                    borderRadius: '10px',
                    background: 'linear-gradient(45deg, var(--primary-color), var(--secondary-color))',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    boxShadow: '0 4px 12px rgba(102, 126, 234, 0.3)'
                }}>
                    <GroupIcon sx={{ color: 'white', fontSize: '1.2rem' }} />
                </Box>
                <Typography variant="h6" sx={{
                    fontWeight: 600,
                    background: 'linear-gradient(45deg, var(--primary-color), var(--secondary-color))',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    backgroundClip: 'text'
                }}>
                    Détails du groupe
                </Typography>
            </DialogTitle>
            <DialogContent dividers>
                {/* Infos principales du groupe */}
                <Box sx={{ mb: 3 }}>
                    <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
                        {group.name}
                    </Typography>
                    <Typography variant="body1" color="text.secondary" gutterBottom>
                        {group.description || 'Pas de description'}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        Créé le : {new Date(group.createdAt).toLocaleDateString('fr-FR')}
                    </Typography>
                </Box>

                <Divider sx={{ my: 2 }} />

                {/* Section membres */}
                <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
                    <Typography variant="h6">👥 Membres</Typography>
                    <Badge color="primary" badgeContent={members.length} showZero />
                </Stack>

                {/* Barre de recherche */}
                {members.length > 5 && (
                    <TextField
                        label="Rechercher un membre"
                        variant="outlined"
                        size="small"
                        fullWidth
                        value={search}
                        onChange={e => setSearch(e.target.value)}
                        sx={{ mb: 2 }}
                    />
                )}

                {/* Liste scrollable des membres */}
                <Box sx={{ maxHeight: 300, overflowY: 'auto' }}>
                    {filteredMembers.length === 0 ? (
                        <Typography variant="body2" color="text.secondary">Aucun membre trouvé</Typography>
                    ) : (
                        <List dense>
                            {filteredMembers.map(user => (
                                <ListItem key={user.uuid}>
                                    <ListItemAvatar>
                                        <Avatar>
                                            {user.firstname?.charAt(0)}{user.lastname?.charAt(0)}
                                        </Avatar>
                                    </ListItemAvatar>
                                    <ListItemText
                                        primary={`${user.firstname} ${user.lastname}`}
                                        secondary={user.email}
                                    />
                                </ListItem>
                            ))}
                        </List>
                    )}
                </Box>
            </DialogContent>

            {/* Actions */}
            <DialogActions sx={{
                flexDirection: { xs: 'column', sm: 'row' },
                justifyContent: { xs: 'stretch', sm: 'space-between' },
                alignItems: { xs: 'stretch', sm: 'center' },
                gap: { xs: 1, sm: 2 },
                p: 2
            }}>
                <Button
                    onClick={onClose}
                    sx={{
                        borderRadius: '10px',
                        textTransform: 'none',
                        fontWeight: 600,
                        px: 3,
                        order: { xs: 4, sm: 0 }
                    }}
                >
                    Fermer
                </Button>
                <Box sx={{
                    display: 'flex',
                    flexDirection: { xs: 'column', sm: 'row' },
                    gap: 1,
                    width: { xs: '100%', sm: 'auto' }
                }}>
                    {onClearMembers && members.length > 0 && (
                        <Button
                            color="warning"
                            variant="outlined"
                            onClick={() => {
                                onClose();
                                onClearMembers(group.uuid);
                            }}
                            sx={{
                                borderRadius: '10px',
                                textTransform: 'none',
                                fontWeight: 600,
                                fontSize: { xs: '0.8rem', sm: '0.875rem' }
                            }}
                        >
                            Vider le groupe
                        </Button>
                    )}
                    <Button
                        onClick={() => onEdit(group)}
                        variant="contained"
                        sx={{
                            background: 'linear-gradient(45deg, var(--primary-color), var(--secondary-color))',
                            borderRadius: '10px',
                            textTransform: 'none',
                            fontWeight: 600,
                            fontSize: { xs: '0.8rem', sm: '0.875rem' },
                            boxShadow: '0 4px 15px rgba(102, 126, 234, 0.3)',
                            '&:hover': {
                                boxShadow: '0 6px 20px rgba(102, 126, 234, 0.4)'
                            }
                        }}
                    >
                        Modifier
                    </Button>
                    <Button
                        color="error"
                        onClick={() => onDelete(group.uuid)}
                        variant="contained"
                        sx={{
                            background: 'linear-gradient(45deg, #f5576c, #f093fb)',
                            borderRadius: '10px',
                            textTransform: 'none',
                            fontWeight: 600,
                            fontSize: { xs: '0.8rem', sm: '0.875rem' },
                            boxShadow: '0 4px 15px rgba(245, 87, 108, 0.3)',
                            '&:hover': {
                                boxShadow: '0 6px 20px rgba(245, 87, 108, 0.4)'
                            }
                        }}
                    >
                        Supprimer
                    </Button>
                </Box>
            </DialogActions>
        </Dialog>
    );
};

export default GroupDetailsModal;
